/**
 * The geometry the select tool works in (SRS C4).
 *
 * Pure maths, no canvas and no board: a matrix, a polygon, and the tests that say
 * whether a point or a line is inside a shape. Kept apart from the tool so that
 * "moving a selection puts it exactly where you dropped it" is something a test can
 * prove without a browser.
 *
 * A matrix maps a point as
 *   x' = a·x + c·y + e
 *   y' = b·x + d·y + f
 * which is the same layout the canvas itself uses.
 */

import type { Point, Rect } from '@opal/shared';

export interface Matrix {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly e: number;
  readonly f: number;
}

/** Changes nothing. */
export const IDENTITY: Matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

/** `outer` applied after `inner`. */
export function multiply(outer: Matrix, inner: Matrix): Matrix {
  return {
    a: outer.a * inner.a + outer.c * inner.b,
    b: outer.b * inner.a + outer.d * inner.b,
    c: outer.a * inner.c + outer.c * inner.d,
    d: outer.b * inner.c + outer.d * inner.d,
    e: outer.a * inner.e + outer.c * inner.f + outer.e,
    f: outer.b * inner.e + outer.d * inner.f + outer.f,
  };
}

export function applyToPoint(matrix: Matrix, point: Point): Point {
  return {
    x: matrix.a * point.x + matrix.c * point.y + matrix.e,
    y: matrix.b * point.x + matrix.d * point.y + matrix.f,
  };
}

/** Slides everything by a distance. */
export function translation(dx: number, dy: number): Matrix {
  return { ...IDENTITY, e: dx, f: dy };
}

/** Scales about a point, so that point stays exactly where it is. */
export function scaleAbout(origin: Point, scale: number): Matrix {
  return {
    a: scale,
    b: 0,
    c: 0,
    d: scale,
    e: origin.x - origin.x * scale,
    f: origin.y - origin.y * scale,
  };
}

/** Turns about a point, by an angle in radians. */
export function rotationAbout(origin: Point, angle: number): Matrix {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    a: cos,
    b: sin,
    c: -sin,
    d: cos,
    e: origin.x - origin.x * cos + origin.y * sin,
    f: origin.y - origin.x * sin - origin.y * cos,
  };
}

/**
 * How much bigger a matrix makes things.
 *
 * The square root of the area it multiplies by, which is what a line's thickness has
 * to follow so that scaled-up writing stays writing rather than turning into wire.
 */
export function scaleFactorOf(matrix: Matrix): number {
  return Math.sqrt(Math.abs(matrix.a * matrix.d - matrix.b * matrix.c)) || 1;
}

/** The box two dragged corners describe, whichever way round they were dragged. */
export function rectFromCorners(from: Point, to: Point): Rect {
  return {
    x: Math.min(from.x, to.x),
    y: Math.min(from.y, to.y),
    width: Math.abs(to.x - from.x),
    height: Math.abs(to.y - from.y),
  };
}

/** The box around a set of points, or null if there are none. */
export function boundsOfPoints(points: readonly Point[]): Rect | null {
  const first = points[0];
  if (!first) return null;
  let minX = first.x;
  let minY = first.y;
  let maxX = first.x;
  let maxY = first.y;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** The box around several boxes, or null if there are none. */
export function boundsOfRects(rects: readonly Rect[]): Rect | null {
  const corners: Point[] = [];
  for (const rect of rects) {
    corners.push({ x: rect.x, y: rect.y });
    corners.push({ x: rect.x + rect.width, y: rect.y + rect.height });
  }
  return boundsOfPoints(corners);
}

export function pointInRect(point: Point, rect: Rect): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

/** Grows a box by the same amount on every side. */
export function growRect(rect: Rect, by: number): Rect {
  return {
    x: rect.x - by,
    y: rect.y - by,
    width: rect.width + by * 2,
    height: rect.height + by * 2,
  };
}

/**
 * Whether a point falls inside a closed shape, by the crossing rule: count the times
 * a ray leaving the point crosses the outline, and an odd count means inside.
 *
 * This is what makes the lasso work whatever shape it is drawn in, including one
 * looped back over itself.
 */
export function pointInPolygon(point: Point, polygon: readonly Point[]): boolean {
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; index += 1) {
    const a = polygon[index];
    const b = polygon[previous];
    previous = index;
    if (!a || !b) continue;
    const straddles = a.y > point.y !== b.y > point.y;
    if (!straddles) continue;
    const crossingX = a.x + ((point.y - a.y) * (b.x - a.x)) / (b.y - a.y);
    if (point.x < crossingX) inside = !inside;
  }
  return inside;
}
